import { Model, Factory, hasMany, belongsTo, trait } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'
import { randSentence, rand, randSoonDate, randRecentDate } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import { pageable, filterable } from './utils.js'
import type { MirageServer, MockServer } from './server.js'
import { TaskStatusEnum } from '@/composables/tasks.js'

export type TaskData = SchemaAttributes<'Task'>

export const model = (Model as ModelDefinition<TaskData>).extend({
  patients: hasMany('patient'),
  client: belongsTo('client'),
  user: belongsTo('user'),
})

export const factory = Factory.extend<TaskData>({
  title() {
    return randSentence()
  },
  due() {
    return rand([
      null,
      randSoonDate({ days: 5 }).toISOString(),
      randRecentDate({ days: 5 }).toISOString(),
    ])
  },
  remind() {
    return rand([
      undefined,
      'REMINDER_0',
      'REMINDER_10',
      'REMINDER_30',
      'REMINDER_60',
    ])
  },
  status() {
    return TaskStatusEnum.StatusNotDone
  },
  legacy_url() {
    return '#'
  },
  task_repeat() {
    return false
  },
  afterCreate(task, server) {
    if (!task.user) {
      const user = server.schema.first('user')
      task.update({ user })
    }
  },
  withRepeat: trait({
    afterCreate(task) {
      task.update({
        taskRepeat: true,
      })
    },
  }),
  withPastDue: trait({
    due() {
      return randRecentDate().toISOString()
    },
  }),
  withFutureDue: trait({
    due() {
      return randSoonDate().toISOString()
    },
  }),
  withPatients: trait({
    afterCreate(task, server) {
      const patients = [server.create('patient')]
      task.update({ patients })
    },
  }),
  withClient: trait({
    afterCreate(task, server) {
      const client = server.create('client')
      task.update({ client })
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('task')

  server.get('/tasks', function (this: MirageServer, schema, request) {
    const tasks = schema.all('task')

    // filter first
    const filtered = filterable(tasks, request, {
      'user.id': (item, value) => (item.user as ModelInstance)?.id === value,
    })

    // then paginate
    const { data, meta } = pageable(filtered, request)

    // and finally construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get('/tasks/my_tasks', function (this: MirageServer, schema, request) {
    const tasks = schema.all('task')

    // filter first
    const filtered = filterable(tasks, request)

    // then paginate
    const { data, meta } = pageable(filtered, request)

    // and finally construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
        legacy_urls: { create: '#' },
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('task', 20)
}
